/**
 * 明細行に紐づく画面状態のキーを組み立てる（#3283）。
 *
 * 明細行の状態（バリデーションエラー・照合結果・計算セル）は、
 * 以前は「配列の位置」をキーにしていた。そのため行を途中に挿入・削除して
 * 位置がずれると、キーと実際の行の対応が崩れ、別の行に表示がズレていた。
 *
 * 行データは `index`（`nextRowIdCounter` 由来の一意ID）を持っているので、
 * 位置ではなくこの行IDをキーにする。行を挿入・削除しても行IDは動かないため、
 * 状態は常に元の行に追随する。
 *
 * 行IDは初期ロード時（PatternAiOcrResult の buildFormData）に
 * 「OCR時点の行位置」がそのまま入る。Functions が review_issues の path を
 * 作るときも同じ OCR 時点の行位置なので、行IDで review_issues を引ける。
 * 画面で追加した行は既存の最大行ID+1 以降が採番され、review_issues の
 * path とは衝突しない（OCR していない行なので要注意も存在しない）。
 *
 * Firestore に保存するときのキー体系（result_data の配列位置）は変えない。
 * 保存直前に `toRowPositionByRowId` で行ID→現在の配列位置へ変換する。
 *
 * 保存すると result_data は画面の並び順で書き直され、行ID は保存後の位置に
 * 振り直される。review_issues の path は Functions が OCR 時点の位置で作ったまま
 * 更新されないため、保存時に `remapReviewIssueRows` で参照先を付け替える。
 */

import type { ReviewIssue } from '../../types/reviewIssue';

/**
 * 行IDの取り出しに必要な最小の形。
 * DynamicFieldValue（api 層で定義）に依存させないため、ここで構造だけを受ける。
 */
interface RowWithId {
  index?: string | number | Date | null;
}

/** 行データから行ID（一意ID）を取り出す。未設定なら配列位置で代用する */
export const getRowId = (item: RowWithId | undefined, fallbackIndex: number): number =>
  typeof item?.index === 'number' ? item.index : fallbackIndex;

/** 明細セルのバリデーションエラーキー */
export const arrayFieldErrorKey = (
  parentValue: string,
  rowId: number,
  childValue: string,
): string => `${parentValue}-${rowId}-${childValue}`;

/** マスタ照合の選択キー（selectedMasterIds / unlinkedMasterKeys 共通） */
export const masterSelectionKey = (masterId: string, rowId: number): string =>
  `${masterId}-${rowId}`;

/** 辞書変換の選択キー */
export const dictionarySelectionKey = (dictionaryId: string, rowId: number): string =>
  `${dictionaryId}-${rowId}`;

/** 紐付け解除キー。保存側（update.ts）も同じ形式で行IDを使う */
export const clearedReasonItemKey = (childId: string, rowId: number): string =>
  `${childId}.${rowId}`;

/**
 * review_issues の path キー。
 * Functions 側（functions/src/utils/pattern/reviewIssues.ts）が生成する形式に合わせる。
 * rowId には OCR 時点の行位置（＝初期ロード時の行ID）を渡す。
 */
export const reviewIssuePathKey = (parentId: string, rowId: number, childId: string): string =>
  `${parentId}[${rowId}].${childId}`;

/**
 * 行ID → 現在の配列位置 の対応表を作る。
 * Firestore へ保存するときに、行IDベースの画面状態を位置ベースへ戻すのに使う。
 */
export const toRowPositionByRowId = (rows: RowWithId[] | undefined): Map<number, number> => {
  const map = new Map<number, number>();
  (rows ?? []).forEach((row, position) => {
    map.set(getRowId(row, position), position);
  });
  return map;
};

/**
 * 保存で明細行が並び替わったとき、review_issues の参照先行を追従させる（#3283）。
 *
 * review_issues の path（`親ID[行位置].子ID`）と row_index は、OCR 実行時点の
 * 行位置で Functions が生成したもの。保存では Functions の要注意判定は走らないため、
 * 画面で行を挿入・削除して保存すると、result_data の並びだけが変わり
 * path が指す行がズレる（要注意マークが別の行に付く）。
 *
 * 保存時は「行ID → 保存後の配列位置」が分かるので、その対応で path と row_index を
 * 付け替える。判定内容（source / code / message）は一切変えない。
 *
 * 並び替えで行が消えた（削除された）場合は、その行の要注意も取り除く。
 *
 * @param issues 保存前の review_issues
 * @param rowPositionsByParentId 親項目ID → (行ID → 保存後の配列位置)
 */
export const remapReviewIssueRows = (
  issues: ReviewIssue[] | undefined,
  rowPositionsByParentId: Map<string, Map<number, number>>,
): ReviewIssue[] => {
  if (!issues?.length) return [];

  const remapped: ReviewIssue[] = [];
  for (const issue of issues) {
    // 明細（`親ID[行位置].子ID`）以外はそのまま
    const match = /^(.+)\[(\d+)\]\.(.+)$/.exec(issue.path);
    if (!match) {
      remapped.push(issue);
      continue;
    }

    const [, parentId, rowPart, childId] = match;
    const positions = rowPositionsByParentId.get(parentId);
    // 対応表が無い親項目（保存対象外）は触らない
    if (!positions) {
      remapped.push(issue);
      continue;
    }

    const nextPosition = positions.get(Number(rowPart));
    // 削除された行の要注意は落とす
    if (nextPosition === undefined) continue;

    remapped.push({
      ...issue,
      path: reviewIssuePathKey(parentId, nextPosition, childId),
      ...(issue.row_index !== undefined ? { row_index: nextPosition } : {}),
    });
  }
  return remapped;
};
